import { useState } from 'react';

// ─── Icons (inline SVG) ────────────────────────────────────────────────────
const Icon = {
  plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14H6L5,6"/><path d="M10,11v6M14,11v6"/><path d="M9,6V4h6v2"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  close: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  search: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  info: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  chat: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  flow: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><line x1="6.5" y1="10" x2="6.5" y2="17.5"/><line x1="6.5" y1="17.5" x2="14" y2="17.5"/></svg>,
  list: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
};

const VARS = ['{{customer_name}}', '{{business_name}}', '{{agent_name}}'];

const REPLY_TYPES = [
  { id: 'text',     label: 'Text Message',       icon: Icon.chat,  color: '#2563eb' },
  { id: 'workflow', label: 'Trigger Workflow',    icon: Icon.flow,  color: '#7c3aed' },
  { id: 'list',     label: 'Interactive List',   icon: Icon.list,  color: '#0891b2' },
];

const MATCH_MODES = [
  { id: 'exact',    label: 'Exact Match',    desc: 'Triggers only when message matches exactly' },
  { id: 'contains', label: 'Contains',       desc: 'Triggers when message contains the keyword' },
  { id: 'starts',   label: 'Starts With',    desc: 'Triggers when message starts with keyword' },
];

const SAMPLE_WORKFLOWS = ['Lead Capture Flow', 'Order Status Flow', 'Support Ticket Flow', 'Booking Flow'];

// ─── Empty rule template ────────────────────────────────────────────────────
const emptyRule = () => ({
  id: Date.now(),
  keyword: '',
  variations: ['', '', '', '', ''],
  matchMode: 'exact',
  replyType: 'text',
  replyText: '',
  workflow: '',
  listItems: [{ title: '', desc: '' }, { title: '', desc: '' }],
  active: true,
});

// ─── Modal ─────────────────────────────────────────────────────────────────
function Modal({ rule, onSave, onClose }) {
  const [draft, setDraft] = useState(JSON.parse(JSON.stringify(rule)));

  const set = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const setVariation = (i, val) => {
    const v = [...draft.variations];
    v[i] = val;
    set('variations', v);
  };

  const addListItem = () => {
    if (draft.listItems.length >= 10) return;
    set('listItems', [...draft.listItems, { title: '', desc: '' }]);
  };

  const removeListItem = (i) => {
    if (draft.listItems.length <= 1) return;
    set('listItems', draft.listItems.filter((_, idx) => idx !== i));
  };

  const insertVar = (v) => set('replyText', draft.replyText + ' ' + v);

  const isValid = draft.keyword.trim().length > 0 &&
    (draft.replyType === 'text' ? draft.replyText.trim().length > 0 :
     draft.replyType === 'workflow' ? draft.workflow.length > 0 :
     draft.listItems.some(li => li.title.trim()));

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 13, boxSizing: 'border-box',
    fontFamily: 'inherit', outline: 'none', transition: 'border 0.15s',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>
              {rule.keyword ? 'Edit Auto Reply' : 'New Custom Auto Reply'}
            </div>
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>
              Define keyword triggers and automated responses
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#6b7280' }}>
            {Icon.close}
          </button>
        </div>

        <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Keyword ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>
              Primary Keyword <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              value={draft.keyword}
              onChange={e => set('keyword', e.target.value.slice(0, 70))}
              placeholder="e.g. pricing, help, order status"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: draft.keyword.length > 60 ? '#ef4444' : '#9ca3af', marginTop: 4, textAlign: 'right' }}>
              {draft.keyword.length}/70 characters
            </div>
          </div>

          {/* ── Variations ── */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                Keyword Variations
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>(up to 5)</span>
              </label>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#15803d' }}>
                {Icon.info} Triggers on exact match of keyword OR any variation
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {draft.variations.map((v, i) => (
                <input
                  key={i}
                  value={v}
                  onChange={e => setVariation(i, e.target.value.slice(0, 70))}
                  placeholder={`Variation ${i + 1}`}
                  style={{ ...inputStyle, fontSize: 12 }}
                />
              ))}
            </div>
          </div>

          {/* ── Match Mode ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 8 }}>
              Match Mode
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {MATCH_MODES.map(m => (
                <button key={m.id} onClick={() => set('matchMode', m.id)} style={{
                  flex: 1, padding: '8px 10px', border: '1.5px solid',
                  borderColor: draft.matchMode === m.id ? '#25D366' : '#e5e7eb',
                  borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                  background: draft.matchMode === m.id ? '#f0fdf4' : '#fff',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: draft.matchMode === m.id ? '#15803d' : '#374151' }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ── Reply Type ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 8 }}>
              Reply Type <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {REPLY_TYPES.map(rt => (
                <button key={rt.id} onClick={() => set('replyType', rt.id)} style={{
                  flex: 1, padding: '10px 8px', border: '1.5px solid',
                  borderColor: draft.replyType === rt.id ? rt.color : '#e5e7eb',
                  borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                  background: draft.replyType === rt.id ? rt.color + '10' : '#fff',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                }}>
                  <span style={{ color: draft.replyType === rt.id ? rt.color : '#9ca3af' }}>{rt.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: draft.replyType === rt.id ? rt.color : '#6b7280' }}>{rt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Reply Content based on type ── */}
          {draft.replyType === 'text' && (
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>
                Reply Message <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={draft.replyText}
                onChange={e => set('replyText', e.target.value)}
                rows={4}
                placeholder="Type your automated reply here..."
                style={{ ...inputStyle, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>Insert variable:</span>
                {VARS.map(v => (
                  <button key={v} onClick={() => insertVar(v)} style={{
                    fontSize: 11, padding: '3px 9px', borderRadius: 20,
                    border: '1px solid #d1d5db', background: '#f9fafb',
                    cursor: 'pointer', color: '#374151',
                  }}>{v}</button>
                ))}
              </div>
            </div>
          )}

          {draft.replyType === 'workflow' && (
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>
                Select Workflow <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={draft.workflow}
                onChange={e => set('workflow', e.target.value)}
                style={{ ...inputStyle, color: draft.workflow ? '#111' : '#9ca3af' }}
              >
                <option value="">-- Choose a workflow --</option>
                {SAMPLE_WORKFLOWS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
              <div style={{ marginTop: 8, background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#6d28d9' }}>
                {Icon.info} When triggered, the selected workflow will start for this customer conversation.
              </div>
            </div>
          )}

          {draft.replyType === 'list' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                  List Items <span style={{ color: '#9ca3af', fontWeight: 400 }}>({draft.listItems.length}/10)</span>
                </label>
                <button onClick={addListItem} style={{
                  background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0',
                  borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600,
                }}>
                  {Icon.plus} Add Item
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draft.listItems.map((li, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#9ca3af', width: 18, flexShrink: 0, textAlign: 'center' }}>{i + 1}</span>
                    <input
                      value={li.title}
                      onChange={e => {
                        const items = [...draft.listItems];
                        items[i] = { ...items[i], title: e.target.value.slice(0, 24) };
                        set('listItems', items);
                      }}
                      placeholder="Title (24 chars)"
                      style={{ ...inputStyle, flex: 2, fontSize: 12 }}
                    />
                    <input
                      value={li.desc}
                      onChange={e => {
                        const items = [...draft.listItems];
                        items[i] = { ...items[i], desc: e.target.value.slice(0, 72) };
                        set('listItems', items);
                      }}
                      placeholder="Description (optional)"
                      style={{ ...inputStyle, flex: 3, fontSize: 12 }}
                    />
                    {draft.listItems.length > 1 && (
                      <button onClick={() => removeListItem(i)} style={{
                        border: 'none', background: '#fef2f2', color: '#ef4444',
                        borderRadius: 6, padding: '7px', cursor: 'pointer', flexShrink: 0,
                      }}>{Icon.trash}</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Footer ── */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4, borderTop: '1px solid #f3f4f6' }}>
            <button onClick={onClose} style={{
              padding: '9px 20px', border: '1.5px solid #e5e7eb', borderRadius: 8,
              background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}>Cancel</button>
            <button onClick={() => isValid && onSave(draft)} style={{
              padding: '9px 24px', border: 'none', borderRadius: 8,
              background: isValid ? '#25D366' : '#d1d5db', color: '#fff',
              cursor: isValid ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600,
            }}>Save Rule</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Rule Card ─────────────────────────────────────────────────────────────
function RuleCard({ rule, onEdit, onDelete, onToggle }) {
  const rt = REPLY_TYPES.find(r => r.id === rule.replyType);
  const activeVars = rule.variations.filter(v => v.trim());

  return (
    <div style={{
      background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12,
      padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 14,
      opacity: rule.active ? 1 : 0.55, transition: 'opacity 0.2s',
    }}>
      {/* Toggle */}
      <button onClick={() => onToggle(rule.id)} style={{
        width: 38, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', flexShrink: 0,
        background: rule.active ? '#25D366' : '#d1d5db', position: 'relative', transition: '0.2s', marginTop: 2,
      }}>
        <span style={{
          position: 'absolute', top: 3, left: rule.active ? 18 : 3, width: 16, height: 16,
          borderRadius: '50%', background: '#fff', transition: '0.2s', display: 'block',
        }} />
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>"{rule.keyword}"</span>
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 20,
            background: rt.color + '15', color: rt.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {rt.icon} {rt.label}
          </span>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280' }}>
            {MATCH_MODES.find(m => m.id === rule.matchMode)?.label}
          </span>
        </div>

        {activeVars.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Also triggers on:</span>
            {activeVars.map((v, i) => (
              <span key={i} style={{ fontSize: 11, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px', color: '#374151' }}>
                "{v}"
              </span>
            ))}
          </div>
        )}

        {rule.replyType === 'text' && rule.replyText && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280', background: '#f9fafb', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #25D366' }}>
            {rule.replyText.length > 100 ? rule.replyText.slice(0, 100) + '…' : rule.replyText}
          </div>
        )}
        {rule.replyType === 'workflow' && rule.workflow && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#7c3aed' }}>→ {rule.workflow}</div>
        )}
        {rule.replyType === 'list' && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#0891b2' }}>
            {rule.listItems.filter(li => li.title).length} list items configured
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={() => onEdit(rule)} style={{
          border: '1px solid #e5e7eb', background: '#fff', borderRadius: 7,
          padding: '6px 10px', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
        }}>
          {Icon.edit} Edit
        </button>
        <button onClick={() => onDelete(rule.id)} style={{
          border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 7,
          padding: '6px 10px', cursor: 'pointer', color: '#ef4444',
        }}>
          {Icon.trash}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function CustomAutoReply() {
  const [rules, setRules] = useState([
    {
      id: 1, keyword: 'pricing', active: true, matchMode: 'exact', replyType: 'text',
      variations: ['price', 'cost', 'how much', '', ''],
      replyText: 'Hi {{customer_name}}! Our pricing starts at ₹999/month. Reply with DEMO to schedule a free demo.',
      workflow: '', listItems: [],
    },
    {
      id: 2, keyword: 'help', active: true, matchMode: 'contains', replyType: 'list',
      variations: ['support', 'issue', '', '', ''],
      replyText: '', workflow: '',
      listItems: [
        { title: 'Track Order', desc: 'Check your order status' },
        { title: 'Returns & Refunds', desc: 'Start a return request' },
        { title: 'Talk to Agent', desc: 'Connect with our team' },
      ],
    },
  ]);

  const [modal, setModal] = useState(null); // null | rule object
  const [search, setSearch] = useState('');

  const filtered = rules.filter(r =>
    r.keyword.toLowerCase().includes(search.toLowerCase()) ||
    r.variations.some(v => v.toLowerCase().includes(search.toLowerCase()))
  );

  const openNew = () => setModal(emptyRule());
  const openEdit = (rule) => setModal(rule);
  const closeModal = () => setModal(null);

  const saveRule = (draft) => {
    setRules(prev => {
      const exists = prev.find(r => r.id === draft.id);
      return exists ? prev.map(r => r.id === draft.id ? draft : r) : [...prev, draft];
    });
    setModal(null);
  };

  const deleteRule = (id) => setRules(prev => prev.filter(r => r.id !== id));
  const toggleRule = (id) => setRules(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r));

  return (
    <div>
      {/* Info banner */}
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1d4ed8',
        display: 'flex', gap: 8, alignItems: 'flex-start',
      }}>
        <span style={{ flexShrink: 0, marginTop: 1 }}>{Icon.info}</span>
        <span>
          Custom Auto Replies are triggered by <strong>exact customer message matches</strong> — regardless of business hours.
          They take priority after Welcome / OOO messages. You can set 1 primary keyword + up to 5 variations (70 chars each).
        </span>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>{Icon.search}</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search keywords..."
            style={{
              width: '100%', padding: '9px 12px 9px 34px', border: '1.5px solid #e5e7eb',
              borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none',
            }}
          />
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
          {rules.length} / 133 rules
        </div>
        <button onClick={openNew} style={{
          background: '#25D366', color: '#fff', border: 'none', padding: '9px 18px',
          borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {Icon.plus} Add New Rule
        </button>
      </div>

      {/* Rules list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#374151', marginBottom: 6 }}>
            {search ? 'No matching rules found' : 'No auto-reply rules yet'}
          </div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>
            {search ? 'Try a different keyword' : 'Create your first keyword-triggered auto reply'}
          </div>
          {!search && (
            <button onClick={openNew} style={{
              background: '#25D366', color: '#fff', border: 'none', padding: '10px 24px',
              borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
            }}>
              Add First Rule
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(rule => (
            <RuleCard key={rule.id} rule={rule} onEdit={openEdit} onDelete={deleteRule} onToggle={toggleRule} />
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && <Modal rule={modal} onSave={saveRule} onClose={closeModal} />}
    </div>
  );
}